<template>
  <div class="orders-page">
    <div class="page-header">
      <h1>Order Management</h1>
      <div class="header-actions">
        <button @click="refreshOrders" :disabled="orderStore.loading" class="btn btn-primary">
          <span v-if="orderStore.loading">⏳</span>
          <span v-else>🔄</span>
          Refresh
        </button>
      </div>
    </div>

    <!-- Filter Tabs -->
    <div class="filter-tabs">
      <button 
        v-for="(filter, key) in filters" 
        :key="key"
        @click="activeFilter = key"
        :class="['tab-btn', { active: activeFilter === key }]"
      >
        {{ filter.label }}
        <span class="badge badge-secondary" v-if="filter.count !== undefined">{{ filter.count }}</span>
      </button>
    </div>

    <!-- Orders List -->
    <div class="orders-section">
      <div v-if="orderStore.loading" class="loading">
        <div class="spinner"></div>
        <p>Loading orders...</p>
      </div>

      <div v-else-if="filteredOrders.length === 0" class="empty-state">
        <p>No orders found for the selected filter.</p>
      </div>

      <div v-else class="orders-table-container">
        <table class="orders-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Status</th>
              <th>Total</th>
              <th>Created</th>
              <th>Billbee</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="order in filteredOrders" 
              :key="order.id"
              class="order-row"
              :class="getOrderRowClass(order)"
            >
              <td class="order-id">{{ order.id }}</td>
              <td class="customer-name">
                {{ order.customer_name || 'N/A' }}
                <div v-if="order.mirakl_data?.customer?.customer_id" class="customer-id">
                  ID: {{ order.mirakl_data.customer.customer_id }}
                </div>
              </td>
              <td class="order-status">
                <span class="badge" :class="getStatusBadgeClass(order.mirakl_status)">
                  {{ order.mirakl_status }}
                </span>
              </td>
              <td class="order-total">{{ formatCurrency(order.total_price) }}</td>
              <td class="order-date">{{ formatDate(order.created_date) }}</td>
              <td class="billbee-status">
                <div v-if="order.synced_to_billbee" class="status-synced">
                  <span class="status-icon">✅</span>
                  <span class="status-text">In Billbee</span>
                  <div v-if="order.billbee_status" class="billbee-details">
                    <span v-if="order.billbee_status.is_shipped" class="detail shipped">🚚 Shipped</span>
                    <span v-if="order.billbee_status.has_invoice" class="detail invoice">📄 Invoice</span>
                  </div>
                </div>
                <div v-else class="status-not-synced">
                  <span class="status-icon">❌</span>
                  <span class="status-text">Not in Billbee</span>
                </div>
              </td>
              <td class="order-actions">
                <template v-if="order.mirakl_status === 'WAITING_ACCEPTANCE'">
                  <button @click="acceptOrder(order.id)" :disabled="processing" class="btn btn-success btn-sm">
                    Accept
                  </button>
                  <button @click="showRejectModal(order)" :disabled="processing" class="btn btn-danger btn-sm">
                    Reject
                  </button>
                </template>
                
                <button @click="viewOrderDetails(order)" class="btn btn-secondary btn-sm">
                  Details
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Reject Modal -->
    <div v-if="showReject" class="modal-overlay" @click="hideRejectModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>Reject Order</h3>
          <button @click="hideRejectModal" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to reject order <strong>{{ selectedOrder?.id }}</strong>?</p>
          <div class="form-group">
            <label class="form-label">Reason for rejection:</label>
            <select v-model="rejectReason" class="form-control">
              <option value="Out of stock">Out of stock</option>
              <option value="Price error">Price error</option>
              <option value="Product discontinued">Product discontinued</option>
              <option value="Technical issue">Technical issue</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="hideRejectModal" class="btn btn-secondary">Cancel</button>
          <button @click="rejectOrder" :disabled="processing" class="btn btn-danger">
            {{ processing ? 'Rejecting...' : 'Reject Order' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Order Details Modal -->
    <div v-if="showDetails" class="modal-overlay" @click="hideDetailsModal">
      <div class="modal modal-large" @click.stop>
        <div class="modal-header">
          <h3>Order Details - {{ selectedOrder?.id }}</h3>
          <button @click="hideDetailsModal" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <pre>{{ JSON.stringify(selectedOrder, null, 2) }}</pre>
        </div>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="orderStore.error" class="alert alert-danger mt-3">
      {{ orderStore.error }}
    </div>
  </div>
</template>

<script>
import { useOrderStore } from '@/stores/orderStore'
import { ref, computed, onMounted } from 'vue'

export default {
  name: 'Orders',
  setup() {
    const orderStore = useOrderStore()
    const activeFilter = ref('all')
    const processing = ref(false)
    const showReject = ref(false)
    const showDetails = ref(false)
    const selectedOrder = ref(null)
    const rejectReason = ref('Out of stock')
    
    const filters = computed(() => ({
      all: { 
        label: 'All Orders', 
        count: orderStore.orders.length 
      },
      waiting: { 
        label: 'Waiting Acceptance', 
        count: orderStore.waitingAcceptanceOrders.length 
      },
      shipping: { 
        label: 'Shipping', 
        count: orderStore.shippingOrders.length 
      },
      shipped: { 
        label: 'Shipped', 
        count: orderStore.shippedOrders.length 
      },
      errors: { 
        label: 'With Errors', 
        count: orderStore.ordersWithErrors.length 
      }
    }))
    
    const filteredOrders = computed(() => {
      let orders = []
      
      switch (activeFilter.value) {
        case 'waiting':
          orders = orderStore.waitingAcceptanceOrders
          break
        case 'shipping':
          orders = orderStore.shippingOrders
          break
        case 'shipped':
          orders = orderStore.shippedOrders
          break
        case 'errors':
          orders = orderStore.ordersWithErrors
          break
        default:
          orders = orderStore.orders
      }
      
      // Sort by created_date descending (newest first)
      return [...orders].sort((a, b) => {
        const dateA = new Date(a.created_date || a.created_at || 0)
        const dateB = new Date(b.created_date || b.created_at || 0)
        return dateB - dateA // Descending order (newest first)
      })
    })
    
    onMounted(async () => {
      await orderStore.fetchOrders()
    })
    
    return {
      orderStore,
      activeFilter,
      filters,
      filteredOrders,
      processing,
      showReject,
      showDetails,
      selectedOrder,
      rejectReason
    }
  },
  
  methods: {
    async refreshOrders() {
      await this.orderStore.fetchOrders()
    },
    
    async acceptOrder(orderId) {
      if (!confirm('Accept this order?')) {
        return
      }
      
      this.processing = true
      try {
        await this.orderStore.acceptOrder(orderId)
        alert('Order accepted successfully!')
        // Refresh orders to get updated status from server
        await this.orderStore.fetchOrders()
      } catch (error) {
        console.error('Failed to accept order:', error)
        alert('Failed to accept order: ' + (error.response?.data?.error || error.message || 'Unknown error'))
      } finally {
        this.processing = false
      }
    },
    
    showRejectModal(order) {
      this.selectedOrder = order
      this.showReject = true
    },
    
    hideRejectModal() {
      this.showReject = false
      this.selectedOrder = null
      this.rejectReason = 'Out of stock'
    },
    
    async rejectOrder() {
      this.processing = true
      try {
        await this.orderStore.rejectOrder(this.selectedOrder.id, this.rejectReason)
        this.hideRejectModal()
        alert('Order rejected successfully!')
        // Refresh orders to get updated status from server
        await this.orderStore.fetchOrders()
      } catch (error) {
        console.error('Failed to reject order:', error)
        alert('Failed to reject order: ' + (error.response?.data?.error || error.message || 'Unknown error'))
      } finally {
        this.processing = false
      }
    },
    
    viewOrderDetails(order) {
      this.selectedOrder = order
      this.showDetails = true
    },
    
    hideDetailsModal() {
      this.showDetails = false
      this.selectedOrder = null
    },
    
    getOrderRowClass(order) {
      return {
        'order-error': order.sync_errors && order.sync_errors.length > 0,
        'order-synced': order.synced_to_billbee,
        'order-waiting': order.mirakl_status === 'WAITING_ACCEPTANCE'
      }
    },
    
    getStatusBadgeClass(status) {
      const classes = {
        'WAITING_ACCEPTANCE': 'badge-warning',
        'SHIPPING': 'badge-info',
        'SHIPPED': 'badge-success',
        'RECEIVED': 'badge-success',
        'CLOSED': 'badge-secondary',
        'REFUSED': 'badge-danger',
        'CANCELED': 'badge-danger',
        'CANCELLED': 'badge-danger'
      }
      return classes[status] || 'badge-secondary'
    },
    
    formatCurrency(amount) {
      if (!amount) return '€0.00'
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR'
      }).format(amount)
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleString('de-DE')
    }
  }
}
</script>

<style scoped>
.orders-page {
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.page-header h1 {
  margin: 0;
}

.filter-tabs {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid #dee2e6;
}

.tab-btn {
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  color: #6c757d;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tab-btn:hover,
.tab-btn.active {
  color: #0d6efd;
  border-bottom-color: #0d6efd;
}

.orders-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
}

.order-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  transition: all 0.2s;
  border: 1px solid #dee2e6;
}

.order-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.order-card.order-error {
  border-left: 4px solid #dc3545;
}

.order-card.order-waiting {
  border-left: 4px solid #ffc107;
}

.order-card.order-synced {
  border-left: 4px solid #198754;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
}

.order-id {
  font-weight: bold;
  font-size: 1.1rem;
}

.order-details {
  margin-bottom: 1rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.label {
  font-weight: 500;
  color: #6c757d;
}

.billbee-status {
  margin-bottom: 1rem;
  padding: 0.5rem;
  border-radius: 4px;
  background-color: #f8f9fa;
}

.status-indicator {
  font-size: 0.85rem;
  font-weight: 500;
}

.status-indicator.synced {
  color: #198754;
}

.status-indicator.not-synced {
  color: #dc3545;
}

.billbee-details {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
  font-size: 0.8rem;
}

.error-section {
  background-color: #fff5f5;
  border: 1px solid #fecaca;
  border-radius: 4px;
  padding: 0.75rem;
  margin-bottom: 1rem;
}

.error-header {
  font-weight: 500;
  color: #dc3545;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.error-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.error-item {
  font-size: 0.8rem;
}

.error-time {
  color: #6c757d;
  display: block;
  margin-bottom: 0.1rem;
}

.error-message {
  color: #dc3545;
}

.order-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-large {
  max-width: 800px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 0.5rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h3 {
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  padding: 0.5rem 1.5rem 1.5rem;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

/* Table Styles */
.orders-table-container {
  margin-top: 1rem;
  overflow-x: auto;
}

.orders-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.orders-table th {
  background: #f8f9fa;
  font-weight: 600;
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #dee2e6;
  color: #495057;
}

.orders-table td {
  padding: 1rem;
  border-bottom: 1px solid #f1f3f4;
  vertical-align: top;
}

.order-row:hover {
  background: #f8f9fa;
}

.order-row.order-error {
  background: #fff5f5;
  border-left: 3px solid #dc3545;
}

.order-row.order-synced {
  border-left: 3px solid #28a745;
}

.order-row.order-waiting {
  border-left: 3px solid #ffc107;
}

.order-id {
  font-weight: 600;
  color: #495057;
}

.customer-name {
  font-weight: 500;
}

.customer-id {
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

.billbee-status {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.status-synced, .status-not-synced {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.status-synced {
  color: #28a745;
}

.status-not-synced {
  color: #dc3545;
}

.status-icon {
  font-size: 0.9rem;
}

.status-text {
  font-size: 0.85rem;
  font-weight: 500;
}

.billbee-details {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.billbee-details .detail {
  font-size: 0.75rem;
  padding: 0.1rem 0.3rem;
  border-radius: 3px;
  background: #e9ecef;
  color: #495057;
}

.order-actions {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .orders-table-container {
    margin-top: 1rem;
  }
  
  .orders-table th,
  .orders-table td {
    padding: 0.5rem;
    font-size: 0.9rem;
  }
  
  .filter-tabs {
    flex-wrap: wrap;
  }
  
  .order-actions .btn {
    font-size: 0.8rem;
    padding: 0.25rem 0.5rem;
  }
}
</style>